// SPDX-License-Identifier: AGPL-3.0-or-later

import type {KonvoCalendarEvent} from '@app/features/konvo_calendar/state/KonvoCalendar';

interface EventInterval {
	start: number;
	end: number;
}

function eventIntervals(events: ReadonlyArray<KonvoCalendarEvent>): Array<EventInterval> {
	return events.flatMap((event) => {
		if (
			event.is_all_day ||
			event.is_cancelled ||
			event.show_as === 'free' ||
			event.response === 'declined' ||
			!event.start ||
			!event.end
		)
			return [];
		const start = Date.parse(event.start);
		const end = Date.parse(event.end);
		return Number.isFinite(start) && Number.isFinite(end) && start < end ? [{start, end}] : [];
	});
}

/**
 * Returns the end of the continuous block of meetings active at `now`.
 * Overlapping and back-to-back events are treated as one block so presence
 * does not briefly reset between them.
 */
export function getLiveMeetingEnd(events: ReadonlyArray<KonvoCalendarEvent>, now: number = Date.now()): number | null {
	const intervals = eventIntervals(events);
	let blockEnd = intervals.reduce(
		(latest, interval) => (interval.start <= now && now < interval.end ? Math.max(latest, interval.end) : latest),
		0,
	);
	if (blockEnd === 0) return null;

	let extended = true;
	while (extended) {
		extended = false;
		for (const interval of intervals) {
			if (interval.start <= blockEnd && interval.end > blockEnd) {
				blockEnd = interval.end;
				extended = true;
			}
		}
	}
	return blockEnd;
}

/** The next start or end boundary after `now`, used for an exact wake-up. */
export function getNextMeetingBoundary(
	events: ReadonlyArray<KonvoCalendarEvent>,
	now: number = Date.now(),
): number | null {
	let next = Number.POSITIVE_INFINITY;
	for (const interval of eventIntervals(events)) {
		if (interval.start > now) next = Math.min(next, interval.start);
		if (interval.end > now) next = Math.min(next, interval.end);
	}
	return Number.isFinite(next) ? next : null;
}
